import Luminary from '../index';
import type { Scene } from '../core/scene/Scene';
import type { SceneGroup } from '../core/SceneGroup';
import type { OpaqueVideoData } from '../video-data/types';
import { Config } from '../video-data/config';

export type SlideRange = {
    start: number;
    end: number;
};

export type RenderSlice = {
    slideRange: SlideRange;
    startTime: number;
    endTime: number;
    videoData: OpaqueVideoData;
};

function isWithinTolerance(value: number, target: number, tolerance: number): boolean {
    return Math.abs(value - target) <= tolerance;
}

function roundUpToFrameBoundary(timeMS: number, maxMS: number, fps: number): number {
    const numFrames = Math.ceil((timeMS / 1000) * fps);
    return Math.min((numFrames / fps) * 1000, maxMS);
}

/**
 * Replaces the given scenes (which must belong to `sceneGroup`) with empty placeholders that keep
 * each scene's duration and incoming transition, inserting them in order starting at
 * `insertAtIndex`. This preserves the scene group's exact internal layout and total duration - so
 * the captions and the voiceover/talking-head media stay in sync - while the placeholders carry no
 * assets (the slide-level `children`, where the heavy media lives, and the scene-level `stickers`
 * are dropped). They sit outside the rendered range and are never drawn, so only their timing
 * matters. Returns the created placeholder scenes, in order.
 */
function replaceScenesWithEmptyPlaceholders(
    sceneGroup: SceneGroup,
    scenesToReplace: Scene[],
    insertAtIndex: number,
): Scene[] {
    const placeholders = scenesToReplace.map((scene) => ({
        data: { ...scene.save(), children: [], stickers: [] },
        duration: scene.duration,
        transitionUrl: scene.customTransition!.transitionUrl,
        transitionDuration: scene.customTransition!.transitionDuration,
    }));

    // Both callers already run this inside `dataLuminary.batchRun`, which is an action, so the removals and
    // insertions below are one atomic change to a derivation without needing another wrapper here.
    scenesToReplace.forEach((scene) => {
        sceneGroup.removeScene(scene.id);
    });

    return placeholders.map((placeholder, offset) => {
        const emptyScene = sceneGroup.addScene({ data: placeholder.data });
        sceneGroup.moveSceneToIndex(emptyScene.id, insertAtIndex + offset);
        emptyScene.duration = placeholder.duration;
        emptyScene.customTransition!.transitionUrl = placeholder.transitionUrl;
        emptyScene.customTransition!.transitionDuration = placeholder.transitionDuration;
        return emptyScene;
    });
}

// A morph is built from both scenes at the cut: its end values are read off the next scene's assets,
// and pairing with those assets is what holds that scene's own intro animations at rest. Either side
// going missing changes what the other one renders - the morph never runs, or the target replays an
// entry animation the whole video doesn't show - so the range below widens to keep both scenes, with
// their assets, whichever side of the cut the rendered window lands on.
//
// A morph chain is atomic here: keeping one neighbour is not enough in either direction. Backwards, a
// morph target inherits its media time offset from its source, which inherits from its own source, so
// every earlier scene decides which frame of a video the rendered scene shows. Forwards, a match cut
// parks frozen duplicates of the next scene's assets in the scene before it, and those duplicates are
// part of the target pool the earlier cut pairs against - so a scene two cuts away still changes what
// the rendered morph does.
function keepMorphChainStart(firstRenderedScene: Scene): Scene {
    let chainStart = firstRenderedScene;
    while (chainStart.prevScene?.morphEnabled) {
        chainStart = chainStart.prevScene;
    }
    return chainStart;
}

function keepMorphChainEnd(lastRenderedScene: Scene): Scene {
    let chainEnd = lastRenderedScene;
    while (chainEnd.morphEnabled && chainEnd.nextScene) {
        chainEnd = chainEnd.nextScene;
    }
    return chainEnd;
}

/**
 * Computes the slide range and the adjusted (slice-relative, frame-aligned) start/end times needed
 * to render the [startTime, endTime] window of a video, and returns the (lightened) video data to
 * render. Scene groups whose media/captions span the window are kept whole - the scenes outside the
 * window become empty placeholders so their assets aren't loaded - because that timing is relative
 * to the scene group.
 */
export async function sliceVideoDataForRender(
    videoData: OpaqueVideoData,
    startTime: number,
    endTime: number,
    fps: number,
): Promise<RenderSlice> {
    const dataLuminary = new Luminary({
        fetchResources: false,
        isCloudBrowser: true,
        disableRenderer: true,
    });
    dataLuminary.load(videoData);

    if (dataLuminary.sceneGroups.some((sceneGroup) => sceneGroup._scenes.length === 0)) {
        throw new Error('Video contains scene groups with no scenes');
    }

    const duration = dataLuminary.duration;

    if (startTime > duration) {
        throw Error(
            `startTime has a value of ${startTime} which is larger than duration of ${duration}. Unable to continue`,
        );
    }

    let endTimeMS = endTime;
    if (
        endTimeMS < 0 ||
        endTimeMS > duration ||
        isWithinTolerance(endTimeMS, duration, Config.VIDEO_TIME_PRECISION)
    ) {
        endTimeMS = duration;
    }

    // This is a hack that we probably should fix later.
    // Currently, we have issues both with the captions timing and the TH media video
    // timing when slicing scenes. This is because the duration of the scene group changes
    // as we remove scenes and the caption animations are relative to the sceneGroup,
    // same for the scene group media (voiceover/talking head) that we try to seek.
    const firstScene = keepMorphChainStart(dataLuminary.getActiveScenesAt(startTime)[0]);
    let timeOffsetFromSlicing = firstScene.startTime;
    let firstSceneIndex = firstScene.globalIndex;
    const firstSceneGroup = firstScene.sceneGroup;
    if (
        firstSceneGroup &&
        (firstSceneGroup.mediaAsset || firstSceneGroup.voiceoverAsset) &&
        firstScene.localIndex > 0
    ) {
        dataLuminary.batchRun(() => {
            const scenesToReplace = firstSceneGroup._scenes.filter(
                (_scene, index) => index < firstScene.localIndex,
            );
            const placeholders = replaceScenesWithEmptyPlaceholders(
                firstSceneGroup,
                scenesToReplace,
                0,
            );
            firstSceneIndex = placeholders[0].globalIndex;
            timeOffsetFromSlicing = placeholders[0].startTime;
        });
    }

    let lastSceneIndex: number;
    if (endTimeMS === duration) {
        lastSceneIndex = dataLuminary.scenes.length - 1;
    } else {
        const activeScenes = dataLuminary.getActiveScenesAt(endTimeMS);
        lastSceneIndex = activeScenes[activeScenes.length - 1].globalIndex;
    }

    lastSceneIndex = keepMorphChainEnd(dataLuminary.scenes[lastSceneIndex]).globalIndex;

    // make sure the last slide index isn't part of a scene group, otherwise it will change the
    // duration which will change the timing of the captions and the TH media
    const lastScene = dataLuminary.scenes[lastSceneIndex];
    const lastSceneGroup = lastScene.sceneGroup;
    if (
        lastSceneGroup &&
        (lastSceneGroup.mediaAsset || lastSceneGroup.voiceoverAsset) &&
        lastScene.localIndex < lastSceneGroup._scenes.length - 1
    ) {
        dataLuminary.batchRun(() => {
            const scenesToReplace = lastSceneGroup._scenes.filter(
                (_scene, index) => index > lastScene.localIndex,
            );
            const placeholders = replaceScenesWithEmptyPlaceholders(
                lastSceneGroup,
                scenesToReplace,
                lastScene.localIndex + 1,
            );
            lastSceneIndex = placeholders[placeholders.length - 1].globalIndex;
        });
    }

    // Round the global times up to the frame grid before removing the slice offset, then subtract
    // the offset to express them relative to the sliced video.
    // timeOffsetFromSlicing (the first kept scene's start time) is generally not aligned to a frame
    // boundary, and it differs between the segment that ends at a given boundary and the segment
    // that starts at it (a transition overlapping the boundary makes them pick a different first
    // scene). Rounding the local time would then snap the same global boundary to two different
    // global frames, so adjacent segments overlap or gap when concatenated. Rounding the global
    // time keeps the boundary consistent across segments.
    const adjustedStartTime =
        roundUpToFrameBoundary(startTime, duration, fps) - timeOffsetFromSlicing;
    const adjustedEndTime =
        roundUpToFrameBoundary(endTimeMS, duration, fps) - timeOffsetFromSlicing;

    const modifiedVideoData = dataLuminary.save();
    dataLuminary.destroy();

    return {
        slideRange: { start: firstSceneIndex, end: lastSceneIndex },
        startTime: adjustedStartTime,
        endTime: adjustedEndTime,
        videoData: modifiedVideoData,
    };
}
